import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Search as SearchIcon, Building2, ChevronRight, TrendingDown, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { KPI, SectionCard } from "./AdminJobCards";

const fmtDT = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

const APPT_CFG = {
  scheduled: { cls: "bg-[#457b9d]/15 text-[#1d3557] border-[#457b9d]/30", label: "Scheduled" },
  confirmed: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Confirmed" },
  checked_in: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Checked-in" },
  no_show: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "No-show" },
  cancelled: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Cancelled" },
  converted: { cls: "bg-[#F1FAEE] text-[#027A48] border-[#12B76A]/25", label: "Converted" },
};
const ApptChip = ({ s }) => {
  const c = APPT_CFG[s] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: s || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

export default function AdminAppointments() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const [s, l] = await Promise.all([
      api.get("/admin/ops/appointments/summary").then((r) => r.data).catch(() => null),
      api.get("/admin/ops/appointments", { params: { q: q || undefined, status: status || undefined, limit: 150 } }).then((r) => r.data).catch(() => ({ rows: [] })),
    ]);
    setSummary(s);
    setRows(l.rows || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [status]);

  const kpis = summary?.kpis || {};
  const top = summary?.top_garages || [];

  return (
    <div className="space-y-6" data-testid="admin-appointments-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform Ops</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Appointments</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Cross-garage bookings — today&apos;s slots, weekly volume and conversion trends.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI testId="appt-kpi-today" label="Today" value={kpis.today ?? "—"} Icon={CalendarClock} />
        <KPI testId="appt-kpi-week" label="This Week" value={kpis.this_week ?? "—"} Icon={CalendarClock} tone="blue" />
        <KPI testId="appt-kpi-scheduled" label="Scheduled" value={kpis.scheduled ?? "—"} tone="steel" />
        <KPI testId="appt-kpi-converted" label="Converted" value={kpis.converted ?? "—"} Icon={TrendingUp} tone="green" sub={`${kpis.convert_pct ?? 0}% convert rate`} />
        <KPI testId="appt-kpi-noshow" label="No-shows" value={kpis.no_show ?? "—"} Icon={TrendingDown} tone="red" sub={`${kpis.no_show_pct ?? 0}%`} />
        <KPI testId="appt-kpi-cancelled" label="Cancelled" value={kpis.cancelled ?? "—"} tone="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <SectionCard
            title="Appointments"
            subtitle={`${rows.length} row(s)`}
            right={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
                  <input
                    data-testid="appt-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
                    placeholder="Appt#, customer, vehicle…"
                    className="pl-7 pr-3 h-8 rounded-md border border-[#E4E7EC] bg-white text-[12px] w-56 focus:outline-none focus:border-[#457b9d]"
                  />
                </div>
                <select
                  data-testid="appt-status-filter"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] bg-white text-[12px] focus:outline-none focus:border-[#457b9d]"
                >
                  <option value="">All statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="checked_in">Checked-in</option>
                  <option value="converted">Converted</option>
                  <option value="no_show">No-show</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button onClick={reload} className="h-8 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]">Refresh</button>
              </div>
            }
            testId="appt-table-card"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Appt #</th>
                    <th className="px-4 py-2 font-semibold">Garage</th>
                    <th className="px-4 py-2 font-semibold">Customer</th>
                    <th className="px-4 py-2 font-semibold">Vehicle</th>
                    <th className="px-4 py-2 font-semibold">Scheduled</th>
                    <th className="px-4 py-2 font-semibold">Duration</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">No appointments match.</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} data-testid={`appt-row-${r.id}`} className="hover:bg-[#F7F9FC]">
                      <td className="px-4 py-2 font-mono font-semibold text-[#1d3557]">{r.number}</td>
                      <td className="px-4 py-2">
                        <Link to={`/admin/garages/${r.garage_id}`} className="text-[#457b9d] hover:underline">{r.garage_name}</Link>
                      </td>
                      <td className="px-4 py-2 text-[#1d3557]">{r.customer_name || "—"}</td>
                      <td className="px-4 py-2 font-mono">{r.vehicle_reg || "—"}</td>
                      <td className="px-4 py-2 text-[#667085]">{fmtDT(r.scheduled_at)}</td>
                      <td className="px-4 py-2 font-mono text-[#667085]">{r.duration_min ? `${r.duration_min}m` : "—"}</td>
                      <td className="px-4 py-2"><ApptChip s={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Top Garages" testId="appt-top-garages">
            <div className="divide-y divide-[#F1FAEE]">
              {top.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {top.map((g, i) => (
                <Link key={g.garage_id} to={`/admin/garages/${g.garage_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]"><Building2 size={14} /></div>
                  <div className="flex-1 min-w-0 text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {g.garage_name}</div>
                  <div className="text-[13px] font-mono font-bold text-[#1d3557]">{g.appointments_count}</div>
                  <ChevronRight size={13} className="text-[#a8dadc]" />
                </Link>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Status Breakdown" testId="appt-status-breakdown">
            <div className="p-5 space-y-2">
              {Object.entries(summary?.by_status || {}).sort(([, a], [, b]) => b - a).map(([s, n]) => (
                <div key={s} className="flex items-center gap-3">
                  <ApptChip s={s} />
                  <div className="flex-1 h-1.5 bg-[#F1FAEE] rounded">
                    <div className="h-full bg-[#457b9d] rounded" style={{ width: `${Math.min(100, (n / Math.max(1, kpis.total || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-[12px] font-mono font-bold text-[#1d3557] w-10 text-right">{n}</div>
                </div>
              ))}
              {Object.keys(summary?.by_status || {}).length === 0 && <div className="text-[13px] text-[#667085] text-center">No data</div>}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
